/**
 * OwnerAdmin - Owner-only dashboard for cost tracking and analytics
 * Shows API costs, revenue, profit margins, and usage stats
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  DollarSign, TrendingUp, TrendingDown, Users, Video, Image,
  Bot, Briefcase, BarChart3, PieChart, Calendar, RefreshCw,
  ArrowLeft, Shield, Zap, AlertTriangle, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OwnerAdmin() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [serviceBreakdown, setServiceBreakdown] = useState([]);
  const [providerBreakdown, setProviderBreakdown] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Check if user is owner
  const isOwner = user?.email === 'rossapallooza@gmail.com' || user?.is_owner;

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth?redirect=/owner-admin');
      return;
    }
    
    if (!isOwner) {
      navigate('/');
      return;
    }
    
    fetchAllData();
  }, [user, token, isOwner]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, servicesRes, providersRes, dailyRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/overview`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/costs/by-service?days=30`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/costs/by-provider?days=30`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/costs/daily?days=14`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/costs/recent?limit=20`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setOverview(overviewRes.data);
      setServiceBreakdown(servicesRes.data.services || []);
      setProviderBreakdown(providersRes.data.providers || []);
      setDailyData(dailyRes.data.daily || []);
      setRecentLogs(logsRes.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      setError(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-zinc-400">This page is only accessible to platform owners.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button 
            onClick={() => navigate('/user-dashboard')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Owner Admin</h1>
                <p className="text-zinc-400">Cost tracking & revenue analytics</p>
              </div>
            </div>
            
            <Button
              onClick={fetchAllData}
              variant="outline"
              className="border-zinc-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </motion.div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-red-400">
            {error}
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Revenue */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
              <span className="text-xs text-zinc-500">All Time</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {formatCurrency(overview?.financials?.all_time?.total_revenue || 0)}
            </p>
            <p className="text-sm text-zinc-400">Total Revenue</p>
          </motion.div>

          {/* Total Costs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-400" />
              </div>
              <span className="text-xs text-zinc-500">All Time</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {formatCurrency(overview?.financials?.all_time?.total_cost || 0)}
            </p>
            <p className="text-sm text-zinc-400">API Costs</p>
          </motion.div>

          {/* Profit */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-teal-400" />
              </div>
              <span className="text-xs text-zinc-500">All Time</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {formatCurrency(overview?.financials?.all_time?.total_profit || 0)}
            </p>
            <p className="text-sm text-zinc-400">Net Profit</p>
          </motion.div>

          {/* Users */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
              <span className="text-xs text-green-400">+{overview?.users?.new_this_week || 0} this week</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {overview?.users?.total || 0}
            </p>
            <p className="text-sm text-zinc-400">Total Users</p>
          </motion.div>
        </div>

        {/* 30-Day Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-teal-500/10 to-blue-500/10 border border-teal-500/20 rounded-xl p-6 mb-8"
        >
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-400" />
            Last 30 Days
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-2xl font-bold text-teal-400">
                {formatCurrency(overview?.financials?.last_30_days?.revenue || 0)}
              </p>
              <p className="text-sm text-zinc-400">Revenue</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">
                {formatCurrency(overview?.financials?.last_30_days?.cost || 0)}
              </p>
              <p className="text-sm text-zinc-400">Costs</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">
                {formatCurrency(overview?.financials?.last_30_days?.profit || 0)}
              </p>
              <p className="text-sm text-zinc-400">Profit</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {overview?.financials?.last_30_days?.transactions || 0}
              </p>
              <p className="text-sm text-zinc-400">Transactions</p>
            </div>
          </div>
        </motion.div>

        {/* Content Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
            <Video className="w-8 h-8 text-pink-400" />
            <div>
              <p className="text-xl font-bold text-white">{overview?.content?.total_videos || 0}</p>
              <p className="text-xs text-zinc-400">Videos</p>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
            <Image className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-xl font-bold text-white">{overview?.content?.total_images || 0}</p>
              <p className="text-xs text-zinc-400">Images</p>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
            <Bot className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-xl font-bold text-white">{overview?.content?.total_chatbots || 0}</p>
              <p className="text-xs text-zinc-400">Chatbots</p>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-amber-400" />
            <div>
              <p className="text-xl font-bold text-white">{overview?.content?.total_agent_hires || 0}</p>
              <p className="text-xs text-zinc-400">Agent Hires</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['overview', 'services', 'providers', 'recent'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab 
                  ? 'bg-teal-500 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && dailyData.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-teal-400" />
              Daily Breakdown (Last 14 Days)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Date</th>
                    <th className="text-right py-3 px-4 text-zinc-400 font-medium">Revenue</th>
                    <th className="text-right py-3 px-4 text-zinc-400 font-medium">Cost</th>
                    <th className="text-right py-3 px-4 text-zinc-400 font-medium">Profit</th>
                    <th className="text-right py-3 px-4 text-zinc-400 font-medium">Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyData.map((day) => (
                    <tr key={day.date} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="py-3 px-4 text-white">{formatDate(day.date)}</td>
                      <td className="py-3 px-4 text-right text-green-400">{formatCurrency(day.revenue)}</td>
                      <td className="py-3 px-4 text-right text-red-400">{formatCurrency(day.cost)}</td>
                      <td className="py-3 px-4 text-right text-teal-400">{formatCurrency(day.profit)}</td>
                      <td className="py-3 px-4 text-right text-zinc-300">{day.transactions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {activeTab === 'services' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-teal-400" />
              Breakdown by Service
            </h3>
            {serviceBreakdown.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No data yet. Generate some content to see analytics!</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium">Service</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Revenue</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Cost</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Profit</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Margin</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceBreakdown.map((service) => (
                      <tr key={service.service} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="py-3 px-4 text-white font-medium">{service.service}</td>
                        <td className="py-3 px-4 text-right text-green-400">{formatCurrency(service.total_revenue)}</td>
                        <td className="py-3 px-4 text-right text-red-400">{formatCurrency(service.total_cost)}</td>
                        <td className="py-3 px-4 text-right text-teal-400">{formatCurrency(service.total_profit)}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={`px-2 py-1 rounded text-xs ${
                            service.margin_percent >= 80 ? 'bg-green-500/20 text-green-400' :
                            service.margin_percent >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {service.margin_percent}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-zinc-300">{service.transaction_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'providers' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-teal-400" />
              Breakdown by Provider
            </h3>
            {providerBreakdown.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No data yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {providerBreakdown.map((provider) => (
                  <div key={provider.provider} className="bg-zinc-800/50 rounded-xl p-4">
                    <h4 className="text-white font-semibold mb-3 capitalize">{provider.provider}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Revenue</span>
                        <span className="text-green-400">{formatCurrency(provider.total_revenue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Cost</span>
                        <span className="text-red-400">{formatCurrency(provider.total_cost)}</span>
                      </div>
                      <div className="flex justify-between border-t border-zinc-700 pt-2">
                        <span className="text-zinc-400">Profit</span>
                        <span className="text-teal-400 font-semibold">{formatCurrency(provider.profit)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Transactions</span>
                        <span className="text-white">{provider.transaction_count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'recent' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Recent Transactions</h3>
            {recentLogs.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No transactions yet.</p>
            ) : (
              <div className="space-y-3">
                {recentLogs.map((log) => (
                  <div key={log.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        log.profit > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {log.profit > 0 ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-red-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">{log.service}</p>
                        <p className="text-xs text-zinc-500">{log.provider} • {new Date(log.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400">+{formatCurrency(log.revenue)}</p>
                      <p className="text-xs text-zinc-500">Cost: {formatCurrency(log.cost)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
